import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Copy, FileDown, Pencil } from 'lucide-react';
import PageHeader from '../../../components/Layout/PageHeader.jsx';
import { Button } from '../../../components/ui/FormComponents.jsx';
import { SkeletonCard } from '../../../components/ui/StateComponents.jsx';
import { useChoferById } from '../../../hooks/useChoferes.js';

function DetailRow({ label, value }) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-semibold uppercase tracking-wide text-[rgb(var(--muted))]">{label}</p>
      <p className="text-sm text-[rgb(var(--fg))]">{value ?? '—'}</p>
    </div>
  );
}

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

export default function VerChofer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [{ data, fetching, error }] = useChoferById(id);
  const chofer = data?.chofer;

  const timeline = useMemo(() => {
    if (!chofer) return [];
    const events = [
      {
        label: 'Creación de registro',
        description: `Creado por ${chofer.createdBy || 'sistema'}`,
        date: chofer.createdAt,
      },
      {
        label: 'Última actualización',
        description: `Actualizado por ${chofer.updatedBy || 'sistema'}`,
        date: chofer.updatedAt,
      },
    ];
    if (chofer.deletedAt) {
      events.push({
        label: 'Eliminado',
        description: 'Marcado como eliminado',
        date: chofer.deletedAt,
      });
    }
    return events.filter(event => event.date);
  }, [chofer]);

  const headerActions = chofer ? (
    <div className="flex flex-wrap items-center gap-2">
      <Button type="button" variant="secondary" onClick={() => navigate(`/administracion/choferes/${chofer.id}/editar`)}>
        <Pencil className="mr-2 h-4 w-4" /> Editar
      </Button>
      <Button type="button" variant="secondary" disabled>
        <Copy className="mr-2 h-4 w-4" /> Duplicar
      </Button>
      <Button type="button" variant="secondary" disabled>
        <FileDown className="mr-2 h-4 w-4" /> Exportar PDF
      </Button>
      <Button type="button" variant="ghost" onClick={() => navigate('/administracion/choferes')}>
        <ArrowLeft className="mr-2 h-4 w-4" /> Volver
      </Button>
    </div>
  ) : null;

  return (
    <div className="mx-auto max-w-[1400px] space-y-6 px-4 pb-24">
      <PageHeader
        title={chofer ? chofer.nombreCompleto : 'Chofer'}
        subtitle="Consultá la información completa del chofer y su historial de cambios."
        breadcrumbs={[
          { label: 'Administración', href: '/administracion' },
          { label: 'Choferes', href: '/administracion/choferes' },
          { label: chofer ? chofer.nombreCompleto : 'Detalle' },
        ]}
        actions={headerActions}
      />

      {fetching && !chofer ? (
        <SkeletonCard />
      ) : error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-6 py-4 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
          <p className="font-semibold">No se pudo cargar el chofer.</p>
          <p className="mt-1 text-xs opacity-90">{error.message}</p>
        </div>
      ) : chofer ? (
        <div className="grid gap-6 lg:grid-cols-3">
          <section className="lg:col-span-2 space-y-6">
            <article className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.92)] p-6 shadow-[0_18px_38px_rgba(var(--shadow),0.08)]">
              <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Identificación</h2>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <DetailRow label="Apellido" value={chofer.apellido} />
                <DetailRow label="Nombre" value={chofer.nombre} />
                <DetailRow label="Documento" value={`${chofer.tipoDocumento} ${chofer.nroDocumento}`} />
                <DetailRow label="CUIL / CUIT" value={chofer.cuilCuit} />
                <DetailRow label="Fecha de nacimiento" value={chofer.fechaNacimiento ? new Date(chofer.fechaNacimiento).toLocaleDateString() : '—'} />
                <DetailRow label="Género" value={chofer.genero || '—'} />
                <DetailRow label="Apodo" value={chofer.apodo || '—'} />
              </div>
            </article>

            <article className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.92)] p-6 shadow-[0_18px_38px_rgba(var(--shadow),0.08)]">
              <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Contacto</h2>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <DetailRow label="Teléfono móvil" value={chofer.telefonoMovil} />
                <DetailRow label="Teléfono alternativo" value={chofer.telefonoAlternativo || '—'} />
                <DetailRow label="Email" value={chofer.email || '—'} />
                <DetailRow label="Dirección" value={[chofer.direccionCalle, chofer.direccionNumero, chofer.direccionPiso, chofer.direccionDepto].filter(Boolean).join(' ') || '—'} />
                <DetailRow label="Localidad" value={chofer.localidad || '—'} />
                <DetailRow label="Provincia" value={chofer.provincia || '—'} />
                <DetailRow label="País" value={chofer.pais || '—'} />
              </div>
            </article>

            <article className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.92)] p-6 shadow-[0_18px_38px_rgba(var(--shadow),0.08)]">
              <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Licencias</h2>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <DetailRow label="Clases" value={chofer.licenciaClase?.join(', ') ?? '—'} />
                <DetailRow label="Fecha emisión" value={chofer.licenciaEmision ? new Date(chofer.licenciaEmision).toLocaleDateString() : '—'} />
                <DetailRow label="Fecha vencimiento" value={chofer.licenciaVencimiento ? new Date(chofer.licenciaVencimiento).toLocaleDateString() : '—'} />
                <DetailRow label="Restricciones" value={chofer.licenciaRestricciones || '—'} />
                <DetailRow label="Apto psicofísico" value={chofer.aptoPsicofisico ? 'Apto' : 'Pendiente'} />
                <DetailRow label="Fecha apto" value={chofer.aptoFecha ? new Date(chofer.aptoFecha).toLocaleDateString() : '—'} />
              </div>
            </article>

            <article className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.92)] p-6 shadow-[0_18px_38px_rgba(var(--shadow),0.08)]">
              <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Documentación</h2>
              <div className="mt-4 grid grid-cols-1 gap-3">
                <DetailRow label="DNI frente" value={chofer.docDniFrenteUrl || '—'} />
                <DetailRow label="DNI dorso" value={chofer.docDniDorsoUrl || '—'} />
                <DetailRow label="Licencia" value={chofer.docLicenciaUrl || '—'} />
                <DetailRow label="Antecedentes" value={chofer.docAntecedentesUrl || '—'} />
                <DetailRow label="ART" value={chofer.docARTUrl || '—'} />
                <DetailRow label="Otros documentos" value={chofer.docOtros?.join(', ') || '—'} />
              </div>
            </article>

            <article className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.92)] p-6 shadow-[0_18px_38px_rgba(var(--shadow),0.08)]">
              <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Observaciones</h2>
              <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-[rgb(var(--fg))]">
                {chofer.observaciones || 'Sin observaciones registradas.'}
              </p>
            </article>
          </section>

          <aside className="space-y-4">
            <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.92)] p-6 shadow-[0_18px_38px_rgba(var(--shadow),0.08)]">
              <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Datos laborales</h2>
              <div className="mt-4 space-y-4">
                <DetailRow label="Empresa" value={chofer.empresaNombre || chofer.empresaId} />
                <DetailRow label="Sucursal" value={chofer.sucursalNombre || chofer.sucursalId} />
                <DetailRow label="Estado" value={chofer.deletedAt ? 'Eliminado' : chofer.estado} />
                <DetailRow label="Fecha de alta" value={chofer.fechaAlta ? new Date(chofer.fechaAlta).toLocaleDateString() : '—'} />
              </div>
            </section>

            <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.92)] p-6 shadow-[0_18px_38px_rgba(var(--shadow),0.08)]">
              <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Timeline</h2>
              <ol className="mt-4 space-y-3">
                {timeline.length === 0 ? (
                  <li className="text-sm text-[rgb(var(--muted))]">Sin actividad registrada.</li>
                ) : (
                  timeline.map(event => (
                    <li key={event.label} className="relative pl-5 text-sm text-[rgb(var(--fg))]">
                      <span className="absolute left-0 top-1.5 h-2 w-2 rounded-full bg-[rgb(var(--brand))]" />
                      <p className="font-semibold">{event.label}</p>
                      <p className="text-xs text-[rgb(var(--muted))]">{event.description}</p>
                      <p className="text-xs text-[rgb(var(--muted))]">{formatDateTime(event.date)}</p>
                    </li>
                  ))
                )}
              </ol>
            </section>
          </aside>
        </div>
      ) : null}
    </div>
  );
}
